import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import clsx from 'clsx';
import { Formik } from 'formik';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Grid,
  MenuItem,
  Switch,
  Typography,
} from '@material-ui/core';
import { Redirect } from 'react-router';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import esLocale from 'date-fns/locale/es';
import useStyles from './styles';
import CustomTextField from '../../../components/Inputs/TextField/CustomTextField';
import handleApiResponse from '../../../utils/handleApiResponse';
import { updatePaymentLink } from '../../../requests/api/paymentLinks';

const STATUS_OPTIONS = ['activo', 'expirado', 'cancelado'];
const TYPE_OPTIONS = ['unico'];

function EditPaymentLinkForm({ className, link, ...rest }) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [submitSuccess, setSubmitSuccess] = useState(false);

  if (submitSuccess) {
    return <Redirect to="/links-de-pago" />;
  }

  return (
    <Formik
      initialValues={{
        name: link.name,
        externalId: link.externalId,
        status: link.status,
        expiresAt: new Date(link.expiresAt),
        amount: link.amount,
        exempt: link.exempt || false,
        type: link.type,
        maxUses: link.maxUses,
        description: link.description || '',
      }}
      validationSchema={Yup.object().shape({
        name: Yup.string().required('Debe ingresar el nombre'),
        externalId: Yup.string().required('Debe ingresar el ID'),
        amount: Yup.number()
          .typeError('Debe ingresar un número')
          .min(0, 'El monto debe ser positivo')
          .required('Debe ingresar el monto'),
        maxUses: Yup.number()
          .typeError('Debe ingresar un número')
          .min(1, 'Debe ser al menos 1')
          .required('Debe ingresar el máximo de usos'),
        expiresAt: Yup.date().required('Debe ingresar la fecha de expiración'),
        description: Yup.string(),
      })}
      onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
        try {
          const response = await updatePaymentLink(link.id, values);
          handleApiResponse(enqueueSnackbar, response, true);
          setStatus({ success: true });
          setSubmitting(false);
          setSubmitSuccess(true);
        } catch (error) {
          handleApiResponse(enqueueSnackbar, error, false);
          setStatus({ success: false });
          setErrors({ submit: error.message });
          setSubmitting(false);
        }
      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
        setFieldValue,
      }) => (
        <form
          className={clsx(classes.emptyRoot, className)}
          onSubmit={handleSubmit}
          {...rest}
        >
          <Card>
            <CardContent>
              <Grid container spacing={3}>
                <Grid item md={6} xs={12}>
                  <CustomTextField
                    fullWidth
                    label="Nombre"
                    name="name"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.name}
                    variant="outlined"
                    error={Boolean(touched.name && errors.name)}
                    helperText={touched.name && errors.name}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <CustomTextField
                    fullWidth
                    label="ID"
                    name="externalId"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.externalId}
                    variant="outlined"
                    error={Boolean(touched.externalId && errors.externalId)}
                    helperText={touched.externalId && errors.externalId}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <CustomTextField
                    fullWidth
                    label="URL"
                    value={link.url}
                    variant="outlined"
                    disabled
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <CustomTextField
                    select
                    fullWidth
                    label="Estado"
                    name="status"
                    value={values.status}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    variant="outlined"
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </MenuItem>
                    ))}
                  </CustomTextField>
                </Grid>
                <Grid item md={6} xs={12}>
                  <LocalizationProvider
                    dateAdapter={AdapterDateFns}
                    adapterLocale={esLocale}
                  >
                    <DatePicker
                      views={['day', 'month', 'year']}
                      label="Fecha de Expiración"
                      value={values.expiresAt}
                      onChange={(value) => setFieldValue('expiresAt', value)}
                      renderInput={(params) => (
                        <CustomTextField
                          {...params}
                          variant="outlined"
                          fullWidth
                          error={Boolean(touched.expiresAt && errors.expiresAt)}
                          helperText={touched.expiresAt && errors.expiresAt}
                        />
                      )}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item md={6} xs={12}>
                  <CustomTextField
                    fullWidth
                    label="Monto"
                    name="amount"
                    type="number"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.amount}
                    variant="outlined"
                    error={Boolean(touched.amount && errors.amount)}
                    helperText={touched.amount && errors.amount}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <Box display="flex" flexDirection="column">
                    <Typography variant="caption" color="textSecondary">
                      Monto Exento
                    </Typography>
                    <FormControlLabel
                      control={(
                        <Switch
                          checked={values.exempt}
                          onChange={(e) => setFieldValue('exempt', e.target.checked)}
                          name="exempt"
                          color="primary"
                        />
                      )}
                      label={values.exempt ? 'Exento' : 'No exento'}
                    />
                  </Box>
                </Grid>
                <Grid item md={6} xs={12}>
                  <CustomTextField
                    fullWidth
                    label="Max Usos"
                    name="maxUses"
                    type="number"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.maxUses}
                    variant="outlined"
                    error={Boolean(touched.maxUses && errors.maxUses)}
                    helperText={touched.maxUses && errors.maxUses}
                    inputProps={{ min: 1 }}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <CustomTextField
                    select
                    fullWidth
                    label="Tipo"
                    name="type"
                    value={values.type}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    variant="outlined"
                  >
                    {TYPE_OPTIONS.map((t) => (
                      <MenuItem key={t} value={t}>
                        {t.charAt(0).toUpperCase() + t.slice(1)}
                      </MenuItem>
                    ))}
                  </CustomTextField>
                </Grid>
                <Grid item xs={12}>
                  <CustomTextField
                    fullWidth
                    label="Descripción"
                    name="description"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.description}
                    variant="outlined"
                    multiline
                    minRows={4}
                    error={Boolean(touched.description && errors.description)}
                    helperText={touched.description && errors.description}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      disabled={isSubmitting}
                    >
                      Confirmar Cambios
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </form>
      )}
    </Formik>
  );
}

EditPaymentLinkForm.propTypes = {
  className: PropTypes.string,
  link: PropTypes.object.isRequired,
};

export default EditPaymentLinkForm;
